import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { GeoSearchControl, OpenStreetMapProvider } from 'leaflet-geosearch';
import 'leaflet-geosearch/dist/geosearch.css';

// Fix para los iconos de Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Componente para centrar el mapa solo cuando cambia el país
function ChangeView({ center, zoom, shouldUpdate }) {
  const map = useMap();
  useEffect(() => {
    if (shouldUpdate) {
      map.setView(center, zoom);
    }
  }, [center, zoom, map, shouldUpdate]);
  return null;
}

// Componente para agregar el control de búsqueda al mapa
function SearchControl({ setPosition, getAddressFromCoordinates, setShouldUpdateView, selectedCountry }) {
  const map = useMap();
  
  useEffect(() => {
    const provider = new OpenStreetMapProvider({
      params: {
        countrycodes: selectedCountry === 'Colombia' ? 'co' : selectedCountry === 'México' ? 'mx' : 'cl', // Código ISO para Colombia, México o Chile
      },
    });
    
    const searchControl = new GeoSearchControl({
      provider: provider,
      style: 'bar',
      showMarker: false, // No mostrar marcador automático para poder usar nuestro propio marcador
      showPopup: false,
      autoClose: true,
      searchLabel: `Buscar en ${selectedCountry}...`,
      keepResult: true
    });
    
    map.addControl(searchControl);
    
    // Capturar el evento de resultado de búsqueda
    map.on('geosearch/showlocation', (e) => {
      const { x, y, label } = e.location;
      setPosition([y, x]); // y es latitud, x es longitud en este caso
      setShouldUpdateView(false);
      getAddressFromCoordinates(y, x);
    });
    
    return () => {
      map.removeControl(searchControl);
    };
  }, [map, setPosition, getAddressFromCoordinates, setShouldUpdateView, selectedCountry]);
  
  return null;
}

// Componente para manejar eventos del mapa
function MapEvents({ onMapClick }) {
  useMapEvents({
    click: (e) => {
      onMapClick(e);
    }
  });
  return null;
}

function LocationPicker({ onLocationSelect }) {
  const [selectedCountry, setSelectedCountry] = useState('Colombia');
  const [position, setPosition] = useState(null);
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [shouldUpdateView, setShouldUpdateView] = useState(true);
  const [fullAddressData, setFullAddressData] = useState(null);

  // Nuevos estados para los campos editables
  const [locationFields, setLocationFields] = useState({
    city: '',
    country: '',
    department: '', // state en la API
    district: '', // neighbourhood en la API
    postal_code: ''
  });
  const [fieldErrors, setFieldErrors] = useState({});

  // Centros de los países
  const countryCenters = {
    'Colombia': { lat: 4.5709, lng: -74.2973, zoom: 6 },
    'México': { lat: 23.6345, lng: -102.5528, zoom: 5 },
    'Chile': { lat: -33.4489, lng: -70.6693, zoom: 6 }
  };

  // Función para obtener la dirección a partir de las coordenadas
  const getAddressFromCoordinates = async (lat, lng) => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
      );
      const data = await response.json();
      
      // Guardamos los datos completos para propósitos informativos
      setFullAddressData(data);
      
      if (data.address) {
        const city = data.address.city || data.address.town || data.address.village || '';
        const country = data.address.country || '';
        const state = data.address.state || '';
        const road = data.address.road || '';
        const postcode = data.address.postcode || '';
        const neighbourhood = data.address.neighbourhood || data.address.suburb || '';
        
        // Llenar los campos editables con los datos de la API
        const newLocationFields = {
          city: city,
          country: country,
          department: state,
          district: neighbourhood,
          postal_code: postcode
        };
        
        setLocationFields(newLocationFields);
        setAddress(`${city}, ${country}`);
        
        // Validar campos obligatorios y enviar al componente padre
        validateAndSendLocation(lat, lng, newLocationFields);
      }
    } catch (error) {
      console.error('Error al obtener la dirección:', error);
    } finally {
      setLoading(false);
    }
  };

  // Función para validar campos obligatorios
  const validateRequiredFields = (fields) => {
    const errors = {};
    if (!fields.city.trim()) errors.city = 'La ciudad es obligatoria';
    // Removemos la validación de country y department ya que son de solo lectura
    // if (!fields.country.trim()) errors.country = 'El país es obligatorio';
    // if (!fields.department.trim()) errors.department = 'El departamento/estado es obligatorio';
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Función para validar y enviar ubicación al componente padre
  const validateAndSendLocation = (lat, lng, fields = locationFields) => {
    if (validateRequiredFields(fields)) {
      onLocationSelect({
        latitude: lat,
        longitude: lng,
        city: fields.city,
        country: fields.country,
        state: fields.department,
        road: fullAddressData?.address?.road || '',
        postcode: fields.postal_code,
        neighbourhood: fields.district,
        display_name: fullAddressData?.display_name || '',
        address_details: fullAddressData?.address,
        isValid: true
      });
    } else {
      onLocationSelect({
        latitude: lat,
        longitude: lng,
        ...fields,
        isValid: false
      });
    }
  };

  // Manejador para cambios en los campos editables
  const handleFieldChange = (fieldName, value) => {
    const newFields = {
      ...locationFields,
      [fieldName]: value
    };
    setLocationFields(newFields);
    
    // Si tenemos una posición seleccionada, actualizar la validación
    if (position) {
      validateAndSendLocation(position[0], position[1], newFields);
    }
  };

  const handleMapClick = (e) => {
    const { lat, lng } = e.latlng;
    setPosition([lat, lng]);
    setShouldUpdateView(false); // Evitar que el mapa se reinicie al hacer clic
    getAddressFromCoordinates(lat, lng);
  };

  const handleCountryChange = (e) => {
    setSelectedCountry(e.target.value);
    setShouldUpdateView(true); // Permitir que el mapa se actualice cuando cambiamos de país
  };

  return (
    <div className="location-picker">
      <div className="country-selector">
        <label htmlFor="country">País:</label>
        <select
          id="country"
          value={selectedCountry}
          onChange={handleCountryChange}
          className="form-select"
        >
          <option value="Colombia">Colombia</option>
          <option value="México">México</option>
          <option value="Chile">Chile</option>
        </select>
      </div>

      <div className="map-container">
        <MapContainer
          center={[countryCenters[selectedCountry].lat, countryCenters[selectedCountry].lng]}
          zoom={countryCenters[selectedCountry].zoom}
          style={{ height: '400px', width: '100%' }}
        >
          <ChangeView 
            center={[countryCenters[selectedCountry].lat, countryCenters[selectedCountry].lng]} 
            zoom={countryCenters[selectedCountry].zoom}
            shouldUpdate={shouldUpdateView}
          />
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          {position && <Marker position={position} />}
          <MapEvents onMapClick={handleMapClick} />
          <SearchControl 
            setPosition={setPosition} 
            getAddressFromCoordinates={getAddressFromCoordinates} 
            setShouldUpdateView={setShouldUpdateView}
            selectedCountry={selectedCountry}
          />
        </MapContainer>
      </div>

      <div className="location-info">
        {loading ? (
          <div className="loading">Cargando información...</div>
        ) : position ? (
          <div className="location-form">
            <div className="form-header">
              <strong>Información de Ubicación</strong>
              <p className="form-subtitle">
                El país y departamento se obtienen automáticamente del mapa. Revisa y completa la ciudad si es necesario.
              </p>
            </div>
            
            <div className="location-fields">
              <div className="field-row">
                <div className="field-group">
                  <label htmlFor="city" className="field-label">
                    Ciudad *
                  </label>
                  <input
                    id="city"
                    type="text"
                    className={`field-input ${fieldErrors.city ? 'field-error' : ''}`}
                    value={locationFields.city}
                    onChange={(e) => handleFieldChange('city', e.target.value)}
                    placeholder="Ingresa la ciudad"
                  />
                  {fieldErrors.city && <span className="error-text">{fieldErrors.city}</span>}
                </div>

                <div className="field-group">
                  <label htmlFor="country" className="field-label">
                    País *
                  </label>
                  <input
                    id="country"
                    type="text"
                    className="field-input field-readonly"
                    value={locationFields.country}
                    placeholder="Obtenido automáticamente"
                    readOnly
                  />
                  <small className="field-help">
                    Este campo se obtiene automáticamente del mapa
                  </small>
                </div>
              </div>

              <div className="field-row">
                <div className="field-group">
                  <label htmlFor="department" className="field-label">
                    Departamento/Estado *
                  </label>
                  <input
                    id="department"
                    type="text"
                    className="field-input field-readonly"
                    value={locationFields.department}
                    placeholder="Obtenido automáticamente"
                    readOnly
                  />
                  <small className="field-help">
                    Este campo se obtiene automáticamente del mapa
                  </small>
                </div>

                <div className="field-group">
                  <label htmlFor="district" className="field-label">
                    Barrio/Distrito
                  </label>
                  <input
                    id="district"
                    type="text"
                    className="field-input"
                    value={locationFields.district}
                    onChange={(e) => handleFieldChange('district', e.target.value)}
                    placeholder="Ingresa el barrio o distrito"
                  />
                </div>
              </div>

              <div className="field-row">
                <div className="field-group">
                  <label htmlFor="postal_code" className="field-label">
                    Código Postal
                  </label>
                  <input
                    id="postal_code"
                    type="text"
                    className="field-input field-readonly"
                    value={locationFields.postal_code}
                    placeholder="Obtenido automáticamente"
                    readOnly
                  />
                  <small className="field-help">
                    Este campo se llena automáticamente y no es editable
                  </small>
                </div>
              </div>
            </div>
            
            {Object.keys(fieldErrors).length > 0 && (
              <div className="validation-summary">
                <p className="validation-error">
                  ⚠️ Por favor completa todos los campos obligatorios marcados con *
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="instructions">
            Busca una ubicación o haz clic en el mapa para seleccionar la ubicación de la tienda
          </div>
        )}
      </div>

      <style jsx>{`
        .location-picker {
          margin: 20px 0;
        }
        .country-selector {
          margin-bottom: 15px;
        }
        .country-selector label {
          margin-right: 10px;
          font-weight: 500;
        }
        .map-container {
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .location-info {
          margin-top: 15px;
        }
        .loading {
          color: #666;
          font-style: italic;
          padding: 10px;
          background-color: #f8f9fa;
          border-radius: 4px;
        }
        .location-form {
          background-color: #f8f9fa;
          border-radius: 8px;
          padding: 20px;
          border: 1px solid #e9ecef;
        }
        .form-header {
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 1px solid #e9ecef;
        }
        .form-subtitle {
          margin: 5px 0 0;
          color: #666;
          font-size: 0.9rem;
        }
        .location-fields {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }
        .field-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
        }
        .field-group {
          display: flex;
          flex-direction: column;
        }
        .field-label {
          font-weight: 500;
          margin-bottom: 5px;
          color: #333;
          font-size: 0.9rem;
        }
        .field-input {
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 0.95rem;
          background-color: white;
          transition: border-color 0.2s ease;
        }
        .field-input:focus {
          outline: none;
          border-color: #0066cc;
          box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.1);
        }
        .field-input.field-error {
          border-color: #dc3545;
        }
        .field-input.field-readonly {
          background-color: #f8f9fa;
          color: #666;
          cursor: not-allowed;
        }
        .error-text {
          color: #dc3545;
          font-size: 0.8rem;
          margin-top: 3px;
        }
        .field-help {
          color: #666;
          font-size: 0.8rem;
          margin-top: 3px;
        }
        .validation-summary {
          margin-top: 15px;
          padding: 10px;
          background-color: #f8d7da;
          border: 1px solid #f5c6cb;
          border-radius: 4px;
        }
        .validation-error {
          color: #721c24;
          margin: 0;
          font-size: 0.9rem;
        }
        @media (max-width: 576px) {
          .field-row {
            grid-template-columns: 1fr;
          }
          .location-form {
            padding: 15px;
          }
        }
        .instructions {
          color: #666;
          font-style: italic;
          padding: 10px;
          background-color: #f8f9fa;
          border-radius: 4px;
        }
      `}</style>
    </div>
  );
}

export default LocationPicker; 